import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const COLORS = {
  primary: '#FF6B35', bg: '#F8FAFC', white: '#FFFFFF',
  border: '#E2E8F0', text1: '#1E293B', text2: '#64748B', text3: '#94A3B8',
  green: '#10B981', red: '#EF4444', yellow: '#F59E0B', purple: '#8B5CF6',
};

const EMPTY_FORM = {
  company_name: '', email_domain: '',
  category: 'bank', description: '', is_active: true, logo_url: '',
  invite_code: '', balance_rollover: false,
};

const CATEGORIES = [
  { value: 'bank',        label: '🏦 Bank' },
  { value: 'energy',      label: '⚡ Enerji / Neft' },
  { value: 'university',  label: '🎓 Universitet' },
  { value: 'telecom',     label: '📡 Telekommunikasiya' },
  { value: 'insurance',   label: '🛡️ Sığorta' },
  { value: 'government',  label: '🏛️ Dövlət' },
  { value: 'tech',        label: '💻 Texnologiya' },
  { value: 'other',       label: '🏢 Digər' },
];


export default function CorporatePage() {
  const { getAdminToken } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, users: 0 });
  const [activeTab, setActiveTab] = useState('partners'); // 'partners' | 'balance' | 'monitor'

  // Balans yükləmə state
  const [balPartner, setBalPartner]   = useState('');
  const [balEmployees, setBalEmployees] = useState([]);
  const [balLimit, setBalLimit]       = useState('150');
  const [balMonth, setBalMonth]       = useState(new Date().getMonth() + 1);
  const [balYear, setBalYear]         = useState(new Date().getFullYear());
  const [balLoading, setBalLoading]   = useState(false);
  const [balMsg, setBalMsg]           = useState('');
  const [balEmpLoading, setBalEmpLoading] = useState(false);

  // Monitorinq state
  const [reports, setReports]       = useState([]);
  const [repLoading, setRepLoading] = useState(false);
  const [repMonth, setRepMonth]     = useState(new Date().getMonth() + 1);
  const [repYear, setRepYear]       = useState(new Date().getFullYear());
  const [repMsg, setRepMsg]         = useState('');
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_list_partners', { p_admin_token: getAdminToken() });
    const rows = data?.success ? data.rows : [];
    setPartners(rows);

    // Stats
    const active = rows.filter(p => p.is_active).length;
    setStats({ total: rows.length, active, users: 0 });
    setLoading(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      company_name: p.company_name,
      email_domain: p.email_domain || '',
      category: p.category || 'other',
      description: p.description || '',
      is_active: p.is_active,
      logo_url: p.logo_url || '',
      invite_code: p.invite_code || '',
      balance_rollover: p.balance_rollover || false,
    });
    setShowForm(true);
  };

  const loadEmployees = async (partnerId) => {
    if (!partnerId) { setBalEmployees([]); return; }
    setBalEmpLoading(true);
    const { data } = await supabase.rpc('admin_get_corporate_employees', { p_admin_token: getAdminToken(), p_partner_id: partnerId });
    setBalEmployees(data?.success ? data.rows : []);
    setBalEmpLoading(false);
  };

  const handleBalPartnerChange = (pid) => {
    setBalPartner(pid);
    loadEmployees(pid);
  };

  const bulkLoadBalance = async () => {
    if (!balPartner) return setBalMsg('❌ Şirkət seçin');
    if (!balEmployees.length) return setBalMsg('❌ Bu şirkətin işçisi yoxdur');
    const limit = parseFloat(balLimit);
    if (!limit || limit <= 0) return setBalMsg('❌ Düzgün limit daxil edin');
    setBalLoading(true);
    setBalMsg('');
    try {
      const { data, error } = await supabase.rpc('admin_bulk_load_corporate_balances', {
        p_admin_token: getAdminToken(),
        p_partner_id: balPartner,
        p_month: balMonth,
        p_year: balYear,
        p_limit: limit,
        p_employee_ids: balEmployees.map(e => e.id),
      });
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Xəta baş verdi');
      if (data.inserted === 0) {
        setBalMsg(`⚠️ ${balMonth}/${balYear} üçün bütün işçilərin balansı artıq yüklüdür`);
      } else {
        setBalMsg(`✅ ${data.inserted} işçiyə ${limit} ₼ balans yükləndi`);
      }
    } catch (err) {
      setBalMsg('❌ ' + err.message);
    } finally {
      setBalLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMsg('⏳ Logo yüklənir...');
    const ext = file.name.split('.').pop();
    const path = `corp-logos/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('business-photos').upload(path, file, { upsert: true });
    if (upErr) return setMsg('❌ ' + upErr.message);
    const { data } = supabase.storage.from('business-photos').getPublicUrl(path);
    setForm(f => ({ ...f, logo_url: data.publicUrl }));
    setMsg('✅ Logo yükləndi');
  };

  const save = async () => {
    if (!form.company_name.trim()) {
      return setMsg('❌ Şirkət adı mütləqdir');
    }
    if (form.invite_code.trim() && !/^[A-Z0-9_-]{3,30}$/.test(form.invite_code.trim().toUpperCase())) {
      return setMsg('❌ Kod formatı düzgün deyil (böyük hərf, rəqəm, tire, _ — 3-30 simvol)');
    }
    setSaving(true);
    setMsg('');
    const payload = {
      ...form,
      email_domain: form.email_domain.trim().toLowerCase() || null,
      invite_code: form.invite_code.trim().toUpperCase() || null,
    };
    let result;
    if (editing) {
      result = await supabase.rpc('admin_update_partner', { p_admin_token: getAdminToken(), p_id: editing, p_fields: payload });
    } else {
      result = await supabase.rpc('admin_create_partner', { p_admin_token: getAdminToken(), p_fields: payload });
    }
    setSaving(false);
    if (result.error || !result.data?.success) return setMsg('❌ ' + (result.error?.message || result.data?.error));
    setMsg('✅ Saxlandı');
    setShowForm(false);
    load();
  };

  const toggleActive = async (id, current) => {
    const { data } = await supabase.rpc('admin_set_partner_active', { p_admin_token: getAdminToken(), p_id: id, p_active: !current });
    if (!data?.success) return;
    setPartners(prev => prev.map(p => p.id === id ? { ...p, is_active: !current } : p));
  };

  const remove = async (id) => {
    if (!window.confirm('Silmək istədiyinizə əminsiniz?')) return;
    const { data } = await supabase.rpc('admin_delete_partner', { p_admin_token: getAdminToken(), p_id: id });
    if (!data?.success) return;
    setPartners(prev => prev.filter(p => p.id !== id));
  };

  const loadReports = async (m, y) => {
    setRepLoading(true);
    const { data } = await supabase.rpc('admin_get_corporate_cafe_reports', {
      p_admin_token: getAdminToken(), p_month: m, p_year: y,
    });
    setReports(data?.success ? data.rows.map(r => ({
      ...r, businesses: { name: r.business_name }, corporate_partners: { company_name: r.company_name },
    })) : []);
    setRepLoading(false);
  };

  const generateReports = async () => {
    setGenLoading(true); setRepMsg('');
    try {
      const from = `${repYear}-${String(repMonth).padStart(2,'0')}-01`;
      const to   = repMonth === 12
        ? `${repYear + 1}-01-01`
        : `${repYear}-${String(repMonth + 1).padStart(2,'0')}-01`;

      const { data: txData, error: txErr } = await supabase.rpc('admin_get_corporate_transactions_for_period', {
        p_admin_token: getAdminToken(), p_from: from, p_to: to,
      });
      if (txErr) throw new Error(txErr.message);
      if (!txData?.success) throw new Error(txData?.error);
      const txns = txData.rows || [];

      if (!txns.length) { setRepMsg('⚠️ Bu dövr üçün əməliyyat yoxdur'); setGenLoading(false); return; }

      // Qruplaşdır: business_id + partner_id
      const grouped = {};
      for (const t of txns) {
        const bizId = t.business_id || t.business_location_id;
        const key   = `${bizId}::${t.corporate_partner_id}`;
        if (!grouped[key]) grouped[key] = { business_id: bizId, corporate_partner_id: t.corporate_partner_id, total: 0, count: 0 };
        grouped[key].total += t.amount || 0;
        grouped[key].count++;
      }

      let inserted = 0;
      for (const { business_id, corporate_partner_id, total, count } of Object.values(grouped)) {
        const { data, error } = await supabase.rpc('admin_upsert_cafe_report', {
          p_admin_token: getAdminToken(), p_business_id: business_id, p_partner_id: corporate_partner_id,
          p_month: repMonth, p_year: repYear, p_total: total, p_count: count,
        });
        if (!error && data?.success) inserted++;
      }

      setRepMsg(`✅ ${inserted} hesabat yaradıldı`);
      loadReports(repMonth, repYear);
    } catch (err) {
      setRepMsg('❌ ' + err.message);
    } finally {
      setGenLoading(false);
    }
  };

  const markPaid = async (id) => {
    const { data } = await supabase.rpc('admin_mark_cafe_report_paid', { p_admin_token: getAdminToken(), p_report_id: id });
    if (!data?.success) return;
    setReports(prev => prev.map(r => r.id === id ? { ...r, status: 'paid', paid_at: new Date().toISOString() } : r));
  };

  const filtered = partners.filter(p => {
    const name = (p.company_name || '').toLowerCase();
    const domain = (p.email_domain || '').toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || domain.includes(q);
  });

  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || '🏢 Digər';

  return (
    <div style={{ padding: 28, background: COLORS.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text1, margin: 0 }}>🏢 Korporativ Tərəfdaşlar</h1>
          <p style={{ fontSize: 14, color: COLORS.text2, marginTop: 4 }}>
            Korporativ email domenləri ilə qeydiyyat edən istifadəçilərə əlavə xal bonusu
          </p>
        </div>
        <button onClick={openAdd} style={{
          background: COLORS.primary, color: '#fff', border: 'none',
          padding: '11px 22px', borderRadius: 10, fontWeight: 700,
          fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          + Yeni Tərəfdaş
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#F1F5F9', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {[{ id: 'partners', label: '🏢 Tərəfdaşlar' }, { id: 'balance', label: '💳 Balans Yükləmə' }, { id: 'monitor', label: '📊 Monitorinq' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
            background: activeTab === t.id ? COLORS.white : 'transparent',
            color: activeTab === t.id ? COLORS.text1 : COLORS.text2,
            boxShadow: activeTab === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {msg && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${msg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`,
          color: msg.startsWith('✅') ? COLORS.green : COLORS.red, fontWeight: 600, fontSize: 14,
        }}>{msg}</div>
      )}

      {/* ── BALANS YÜKLƏMƏ TAB ── */}
      {activeTab === 'balance' && (
        <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 28, marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.text1, margin: '0 0 20px' }}>💳 İşçilərə Balans Yüklə</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* Şirkət seç */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət</label>
              <select value={balPartner} onChange={e => handleBalPartnerChange(e.target.value)}
                style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                <option value="">— Şirkət seçin —</option>
                {partners.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.company_name}</option>
                ))}
              </select>
            </div>

            {/* Aylıq limit */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Aylıq Limit (₼)</label>
              <input type="number" min="1" value={balLimit} onChange={e => setBalLimit(e.target.value)}
                placeholder="150"
                style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Ay */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Ay</label>
              <select value={balMonth} onChange={e => setBalMonth(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                {['Yanvar','Fevral','Mart','Aprel','May','İyun','İyul','Avqust','Sentyabr','Oktyabr','Noyabr','Dekabr'].map((m, i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
            </div>

            {/* İl */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>İl</label>
              <select value={balYear} onChange={e => setBalYear(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* İşçi siyahısı */}
          {balPartner && (
            <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${COLORS.border}` }}>
              {balEmpLoading ? (
                <p style={{ color: COLORS.text3, fontSize: 14, margin: 0 }}>İşçilər yüklənir...</p>
              ) : balEmployees.length === 0 ? (
                <p style={{ color: COLORS.text3, fontSize: 14, margin: 0 }}>Bu şirkətə qeydiyyatlı işçi yoxdur</p>
              ) : (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, margin: '0 0 10px' }}>
                    {balEmployees.length} işçi tapıldı
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {balEmployees.slice(0, 20).map(e => (
                      <span key={e.id} style={{
                        background: '#fff', border: `1px solid ${COLORS.border}`,
                        borderRadius: 20, padding: '4px 12px', fontSize: 12, color: COLORS.text1, fontWeight: 500,
                      }}>{e.full_name || e.email}</span>
                    ))}
                    {balEmployees.length > 20 && (
                      <span style={{ fontSize: 12, color: COLORS.text3, padding: '4px 0' }}>+{balEmployees.length - 20} daha</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {balMsg && (
            <div style={{
              marginBottom: 16, padding: '12px 16px', borderRadius: 10, fontWeight: 600, fontSize: 14,
              background: balMsg.startsWith('✅') ? '#F0FDF4' : balMsg.startsWith('⚠️') ? '#FFFBEB' : '#FEF2F2',
              color: balMsg.startsWith('✅') ? COLORS.green : balMsg.startsWith('⚠️') ? COLORS.yellow : COLORS.red,
            }}>{balMsg}</div>
          )}

          <button onClick={bulkLoadBalance} disabled={balLoading || !balPartner} style={{
            padding: '13px 32px', borderRadius: 10, border: 'none',
            background: balLoading || !balPartner ? '#E2E8F0' : COLORS.purple,
            color: balLoading || !balPartner ? COLORS.text3 : '#fff',
            fontWeight: 700, fontSize: 15, cursor: balLoading || !balPartner ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}>
            {balLoading ? '⏳ Yüklənir...' : `💳 Hamısına ${balLimit || '?'} ₼ Yüklə`}
          </button>
        </div>
      )}

      {/* ── MONİTORİNQ TAB ── */}
      {activeTab === 'monitor' && (
        <div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 5 }}>Ay</label>
              <select value={repMonth} onChange={e => setRepMonth(Number(e.target.value))}
                style={{ padding: '9px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                {['Yanvar','Fevral','Mart','Aprel','May','İyun','İyul','Avqust','Sentyabr','Oktyabr','Noyabr','Dekabr'].map((m,i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 5 }}>İl</label>
              <select value={repYear} onChange={e => setRepYear(Number(e.target.value))}
                style={{ padding: '9px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                {[2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button onClick={() => loadReports(repMonth, repYear)} style={{
              padding: '9px 18px', borderRadius: 9, border: `1px solid ${COLORS.border}`,
              background: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: COLORS.text2,
            }}>🔍 Axtar</button>
            <button onClick={generateReports} disabled={genLoading} style={{
              padding: '9px 18px', borderRadius: 9, border: 'none',
              background: genLoading ? COLORS.border : COLORS.purple,
              color: genLoading ? COLORS.text3 : '#fff',
              fontWeight: 700, fontSize: 13, cursor: genLoading ? 'not-allowed' : 'pointer',
            }}>{genLoading ? '⏳ Yaradılır...' : '⚡ Hesabat Yarat'}</button>
          </div>

          {repMsg && <div style={{ marginBottom: 16, padding: '11px 16px', borderRadius: 10, fontWeight: 600, fontSize: 14,
            background: repMsg.startsWith('✅') ? '#F0FDF4' : repMsg.startsWith('⚠️') ? '#FFFBEB' : '#FEF2F2',
            color: repMsg.startsWith('✅') ? COLORS.green : repMsg.startsWith('⚠️') ? COLORS.yellow : COLORS.red,
          }}>{repMsg}</div>}

          <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: `1px solid ${COLORS.border}` }}>
                  {['Kafe','Şirkət','Əməliyyat','Məbləğ','Kafe bildirdi','Uyğunluq','Status',''].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: COLORS.text2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repLoading ? (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>Yüklənir...</td></tr>
                ) : reports.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>
                    Hesabat yoxdur — "Hesabat Yarat" düyməsini basın
                  </td></tr>
                ) : reports.map(r => {
                  const match = r.cafe_reported_amount == null ? null
                    : Math.abs(r.cafe_reported_amount - r.total_amount) < 0.01;
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: 14, color: COLORS.text1 }}>{r.businesses?.name || '—'}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: '#EDE9FE', color: COLORS.purple, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 6 }}>
                          {r.corporate_partners?.company_name || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: COLORS.text2 }}>{r.transaction_count}</td>
                      <td style={{ padding: '12px 14px', fontWeight: 800, color: COLORS.purple, fontSize: 15 }}>{r.total_amount?.toFixed(2)} ₼</td>
                      <td style={{ padding: '12px 14px' }}>
                        {r.cafe_reported_amount != null
                          ? <span style={{ fontWeight: 700, color: COLORS.text1 }}>{r.cafe_reported_amount?.toFixed(2)} ₼</span>
                          : <span style={{ color: COLORS.text3, fontSize: 12 }}>Gözlənilir</span>}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {match === null ? <span style={{ color: COLORS.text3 }}>—</span>
                          : match
                            ? <span style={{ color: COLORS.green, fontWeight: 700 }}>✅ Uyğun</span>
                            : <span style={{ color: COLORS.red, fontWeight: 700 }}>❌ Fərq</span>}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          background: r.status === 'paid' ? '#D1FAE5' : '#FEF3C7',
                          color: r.status === 'paid' ? COLORS.green : COLORS.yellow,
                        }}>{r.status === 'paid' ? '✅ Ödənildi' : '⏳ Gözlər'}</span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {r.status !== 'paid' && (
                          <button onClick={() => markPaid(r.id)} style={{
                            padding: '5px 12px', borderRadius: 7, border: 'none',
                            background: COLORS.green, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                          }}>Ödənildi ✓</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'partners' && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Ümumi Tərəfdaş', value: stats.total, color: COLORS.primary, icon: '🏢' },
              { label: 'Aktiv', value: stats.active, color: COLORS.green, icon: '✅' },
              { label: 'Passiv', value: stats.total - stats.active, color: COLORS.text3, icon: '⏸️' },
            ].map((s, i) => (
              <div key={i} style={{ background: COLORS.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 22 }}>{s.icon}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1.2, marginTop: 6 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: COLORS.text2, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div style={{ marginBottom: 16 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Şirkət adı və ya domen axtar..."
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
                border: `1px solid ${COLORS.border}`, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Table */}
          <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: `1px solid ${COLORS.border}` }}>
                  {['Şirkət', 'Şirkət Kodu', 'Kateqoriya', 'Status', 'Əməliyyat'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: COLORS.text2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>Yüklənir...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>
                    {search ? 'Axtarışa uyğun nəticə yoxdur' : 'Hələ heç bir korporativ tərəfdaş yoxdur'}
                  </td></tr>
                ) : filtered.map(p => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.text1 }}>{p.company_name}</div>
                      {p.description && <div style={{ fontSize: 12, color: COLORS.text3, marginTop: 2 }}>{p.description}</div>}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        background: '#EDE9FE', color: '#6C63FF', fontSize: 12,
                        fontWeight: 700, padding: '3px 10px', borderRadius: 6, fontFamily: 'monospace',
                      }}>{p.invite_code || '—'}</span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: COLORS.text2 }}>
                      {getCategoryLabel(p.category)}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button onClick={() => toggleActive(p.id, p.is_active)} style={{
                        padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        background: p.is_active ? '#D1FAE5' : '#F1F5F9',
                        color: p.is_active ? COLORS.green : COLORS.text3,
                      }}>{p.is_active ? '✅ Aktiv' : '⏸️ Passiv'}</button>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => openEdit(p)} style={{
                          padding: '6px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                          background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: COLORS.text2,
                        }}>Düzəlt</button>
                        <button onClick={() => remove(p.id)} style={{
                          padding: '6px 14px', borderRadius: 8, border: 'none',
                          background: '#FEE2E2', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: COLORS.red,
                        }}>Sil</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: COLORS.white, borderRadius: 16, padding: 32, width: '100%',
            maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: COLORS.text1, margin: 0 }}>
                {editing ? 'Tərəfdaşı Düzəlt' : 'Yeni Korporativ Tərəfdaş'}
              </h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: COLORS.text3 }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Şirkət adı */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət Adı *</label>
                <input
                  value={form.company_name}
                  onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="məs: Pasha Bank"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Şirkət kodu (invite_code) */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət Kodu (invite_code)</label>
                <input
                  value={form.invite_code}
                  onChange={e => setForm(f => ({ ...f, invite_code: e.target.value.toUpperCase() }))}
                  placeholder="məs: ŞİRKƏT-2026"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                />
                <p style={{ fontSize: 11, color: COLORS.text3, marginTop: 4 }}>İşçilər qeydiyyatda bu kodu daxil edərək korporativ hesab açar</p>
              </div>

              {/* Email domain (köhnə sistem — ixtiyari) */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Email Domen (köhnə — ixtiyari)</label>
                <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${COLORS.border}`, borderRadius: 9, overflow: 'hidden' }}>
                  <span style={{ padding: '10px 12px', background: '#F8FAFC', color: COLORS.text3, fontSize: 14, borderRight: `1px solid ${COLORS.border}` }}>@</span>
                  <input
                    value={form.email_domain}
                    onChange={e => setForm(f => ({ ...f, email_domain: e.target.value.toLowerCase() }))}
                    placeholder="pashabank.az"
                    style={{ flex: 1, padding: '10px 13px', border: 'none', fontSize: 14, outline: 'none', fontFamily: 'monospace' }}
                  />
                </div>
              </div>

              {/* Balance rollover */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="checkbox" id="balance_rollover"
                  checked={form.balance_rollover}
                  onChange={e => setForm(f => ({ ...f, balance_rollover: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="balance_rollover" style={{ fontSize: 14, color: COLORS.text1, cursor: 'pointer', fontWeight: 600 }}>
                  Balans növbəti aya keçsin (rollover)
                  <span style={{ fontSize: 11, color: COLORS.text3, display: 'block', fontWeight: 400 }}>
                    Aktiv: qalan məbləğ üstünə yeni limit əlavə olunur. Passiv: hər ay sıfırlanır.
                  </span>
                </label>
              </div>

              {/* Kateqoriya */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Kateqoriya</label>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* Açıqlama */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Açıqlama (istəyə bağlı)</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="məs: Əsas tərəfdaş, VIP status"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Logo */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət Logosu</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {form.logo_url && (
                    <img src={form.logo_url} alt="logo" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain', border: `1px solid ${COLORS.border}`, background: '#F8FAFC', padding: 4 }} />
                  )}
                  <label style={{
                    padding: '8px 16px', borderRadius: 9, border: `1px solid ${COLORS.border}`,
                    background: '#F8FAFC', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.text2,
                  }}>
                    {form.logo_url ? 'Dəyişdir' : '+ Logo Yüklə'}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  </label>
                  {form.logo_url && (
                    <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))} style={{ background: 'none', border: 'none', color: COLORS.red, cursor: 'pointer', fontSize: 13 }}>Sil</button>
                  )}
                </div>
              </div>

              {/* Aktiv */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="checkbox" id="is_active"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="is_active" style={{ fontSize: 14, color: COLORS.text1, cursor: 'pointer', fontWeight: 600 }}>
                  Aktiv — qeydiyyat zamanı tanınsın
                </label>
              </div>
            </div>

            {msg && (
              <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
                color: msg.startsWith('✅') ? COLORS.green : COLORS.red,
              }}>{msg}</div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={() => setShowForm(false)} style={{
                flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.border}`,
                background: '#fff', color: COLORS.text2, fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>Ləğv et</button>
              <button onClick={save} disabled={saving} style={{
                flex: 2, padding: '12px', borderRadius: 10, border: 'none',
                background: COLORS.primary, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                opacity: saving ? 0.7 : 1,
              }}>{saving ? 'Saxlanır...' : editing ? 'Yenilə' : 'Əlavə Et'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
